/**
 * DriftTab — environment drift view.
 *
 * Env-pair selector (6 directed combinations, default dev → prod).
 * Fetches GET /drift?from=&to= and renders one collapsible row per service.
 *
 * CORE-1011: added Release Notes button in the controls bar. Clicking it
 * shows a cost-estimate confirm modal computed from the already-loaded
 * `summary_cached` flags on each cell. On confirm, fires
 * GET /drift/release-notes?from=&to= and renders the ReleaseNotesPanel
 * above the per-service rows. Per-service SummaryState was lifted up here
 * from DriftRow so the panel's attention-needed chips can auto-trigger
 * Summarise on a target row.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, getDrift, getDriftSummaryAggregate, getReleaseNotes } from '@/api/client';
import { useRefresh } from '@/context/RefreshContext';
import { DriftRow } from './DriftRow';
import type { SummaryState } from './SummarisePanel';
import { getDriftSummary } from '@/api/client';
import {
  ReleaseNotesPanel,
  ReleaseNotesConfirmModal,
  computeCostEstimate,
  type CostEstimate,
} from './ReleaseNotesPanel';
import {
  DRIFT_DEFAULT_FROM,
  DRIFT_DEFAULT_TO,
  DRIFT_ENV_PAIRS,
  type DriftEnv,
} from '@/utils/driftConfig';
import type { DriftResponse, DriftSummaryAggregateResponse, ReleaseNotesResponse } from '@/types/api';

function pairKey(from: string, to: string): string {
  return `${from}→${to}`;
}

// ---------------------------------------------------------------------------
// Release-notes state machine
//
// idle         → (click "Release Notes") → confirm
// confirm      → (Cancel) → idle
// confirm      → (Continue) → loading
// loading      → (success) → loaded
// loading      → (error) → error
// loaded|error → (click Refresh) → loading
// ---------------------------------------------------------------------------

type ReleaseNotesState =
  | { status: 'idle' }
  | { status: 'confirm'; estimate: CostEstimate }
  | { status: 'loading' }
  | { status: 'loaded'; response: ReleaseNotesResponse }
  | { status: 'error'; message: string };

export function DriftTab(): JSX.Element {
  const { manualRefreshNonce, tabVisible } = useRefresh();
  const [from, setFrom] = useState<DriftEnv>(DRIFT_DEFAULT_FROM as DriftEnv);
  const [to, setTo] = useState<DriftEnv>(DRIFT_DEFAULT_TO as DriftEnv);
  const [data, setData] = useState<DriftResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Aggregate is global (all services/env-pairs), fetched once per tab
  // mount + on manual refresh. Independent of from/to — env-pair changes
  // don't re-fetch. Errors don't block the drift table; the cost strip
  // just hides silently if this fetch fails (aggregate is informational).
  const [aggregate, setAggregate] = useState<DriftSummaryAggregateResponse | null>(null);
  // Per-service summary state — lifted from DriftRow to DriftTab (CORE-1011)
  // so the release-notes panel's attention-needed chips can auto-trigger
  // Summarise on a target row from outside the row itself. Keyed by service
  // name. Preserves prior behavior of surviving row expand/collapse cycles
  // since the state now lives above the row.
  const [summaryStates, setSummaryStates] = useState<Record<string, SummaryState>>({});
  const setSummaryStateFor = useCallback(
    (service: string) => (state: SummaryState): void => {
      setSummaryStates((prev) => ({ ...prev, [service]: state }));
    },
    [],
  );
  // Service to auto-expand+trigger. Set when an attention chip is clicked;
  // DriftRow reads `autoExpand` and scrolls+opens on mount/change.
  const [targetService, setTargetService] = useState<string | null>(null);
  // Release-notes state machine (see type above).
  const [releaseNotesState, setReleaseNotesState] = useState<ReleaseNotesState>({ status: 'idle' });
  // Monotonic counter used to cancel in-flight release-notes fetches.
  // Incremented on every new fetch AND on env-pair change so that a
  // stale response's .then() is a no-op. Event handlers (not effects)
  // fire the fetch, so a useRef is the right tool — changing it never
  // triggers a re-render, and the current value is always fresh inside
  // the .then() closure (which captures the ref object, not a snapshot).
  const rnFetchId = useRef(0);

  // Reset release-notes panel when env pair changes; also invalidate any
  // in-flight fetch so its .then() doesn't override the idle reset.
  useEffect(() => {
    rnFetchId.current++;
    setReleaseNotesState({ status: 'idle' });
  }, [from, to]);

  useEffect(() => {
    if (!tabVisible) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setData(null);

    getDrift(from, to)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setError(`Failed to load drift data (${err.status}).`);
        } else {
          setError('Failed to load drift data.');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return (): void => {
      cancelled = true;
    };
  }, [from, to, tabVisible, manualRefreshNonce]);

  useEffect(() => {
    if (!tabVisible) return undefined;
    let cancelled = false;
    getDriftSummaryAggregate()
      .then((response) => {
        if (!cancelled) setAggregate(response);
      })
      .catch((err: unknown) => {
        // Do NOT set error state or block drift render — cost strip is
        // informational. Also do NOT setAggregate(null): on a REFRESH
        // failure (aggregate was previously loaded, transient 500), the
        // prior value is more useful than a wiped strip. Initial-mount
        // failure just leaves aggregate at its default null.
        // eslint-disable-next-line no-console -- diagnostic
        console.warn('drift-summary aggregate fetch failed:', err);
      });
    return (): void => {
      cancelled = true;
    };
    // Aggregate is env-independent — no `from` / `to` in deps.
  }, [tabVisible, manualRefreshNonce]);

  function onPairChange(event: React.ChangeEvent<HTMLSelectElement>): void {
    const [nextFrom, nextTo] = event.target.value.split('→') as [DriftEnv, DriftEnv];
    setFrom(nextFrom);
    setTo(nextTo);
  }

  function onReleaseNotesClick(): void {
    const cells = data?.cells ?? [];
    const estimate = computeCostEstimate(cells);
    setReleaseNotesState({ status: 'confirm', estimate });
  }

  function onConfirmCancel(): void {
    setReleaseNotesState({ status: 'idle' });
  }

  /**
   * Fire a new release-notes fetch. Increments `rnFetchId` so any
   * concurrent in-flight fetch's .then() is silently discarded — same
   * cancellation pattern as the getDrift useEffect above.
   */
  function fetchReleaseNotes(): void {
    const id = ++rnFetchId.current;
    setReleaseNotesState({ status: 'loading' });
    getReleaseNotes(from, to)
      .then((response) => {
        if (id !== rnFetchId.current) return;
        setReleaseNotesState({ status: 'loaded', response });
      })
      .catch((err: unknown) => {
        if (id !== rnFetchId.current) return;
        const message = err instanceof Error ? err.message : 'Unknown error';
        setReleaseNotesState({ status: 'error', message });
      });
  }

  function onConfirmContinue(): void {
    fetchReleaseNotes();
  }

  function onReleaseNotesRefresh(): void {
    fetchReleaseNotes();
  }

  /**
   * Called by attention-needed chips in ReleaseNotesPanel. Sets the
   * target service so DriftRow auto-expands and scrolls into view, then
   * fires its Summarise fetch if the summary isn't loaded yet.
   */
  function onAttentionServiceClick(service: string): void {
    setTargetService(service);
    const current = summaryStates[service];
    if (current === undefined || current.status === 'idle' || current.status === 'error') {
      setSummaryStateFor(service)({ status: 'loading' });
      getDriftSummary(from, to, service)
        .then((response) => {
          setSummaryStateFor(service)({ status: 'loaded', response });
        })
        .catch((err: unknown) => {
          const message = err instanceof Error ? err.message : 'Unknown error';
          setSummaryStateFor(service)({ status: 'error', message });
        });
    }
    // Clear the target after a tick so DriftRow's autoExpand effect fires
    // on a distinct change when the same service is clicked twice.
    setTimeout(() => setTargetService(null), 0);
  }

  // Stale detection: any per-service summary loaded AFTER the release-notes
  // response was generated means the narrative may be out of date.
  const stale =
    releaseNotesState.status === 'loaded' &&
    Object.values(summaryStates).some((s) => {
      if (s.status !== 'loaded') return false;
      return new Date(s.response.generated_at) > new Date(releaseNotesState.response.generated_at);
    });

  const releaseNotesEnabled = data !== null && !isLoading && error === null;

  return (
    <div className="drift-tab">
      <div className="drift-tab-controls">
        <label htmlFor="drift-pair-selector">Environment pair:</label>
        <select
          id="drift-pair-selector"
          aria-label="Environment pair"
          value={pairKey(from, to)}
          onChange={onPairChange}
        >
          {DRIFT_ENV_PAIRS.map((pair) => (
            <option key={pairKey(pair.from, pair.to)} value={pairKey(pair.from, pair.to)}>
              {pair.from} → {pair.to}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="drift-release-notes-btn"
          disabled={!releaseNotesEnabled}
          onClick={onReleaseNotesClick}
          title={releaseNotesEnabled ? undefined : 'Load drift data first'}
        >
          Release Notes
        </button>
      </div>

      {releaseNotesState.status === 'confirm' && (
        <ReleaseNotesConfirmModal
          from={from}
          to={to}
          estimate={releaseNotesState.estimate}
          onCancel={onConfirmCancel}
          onConfirm={onConfirmContinue}
        />
      )}

      {isLoading && <div className="drift-tab-loading">Loading drift…</div>}
      {error !== null && !isLoading && (
        <div className="drift-tab-error" role="alert">{error}</div>
      )}
      {data !== null && !isLoading && error === null && (
        <>
          {data.errors.length > 0 && (
            <div className="drift-tab-top-errors" role="alert">
              <strong>Errors:</strong>
              <ul>
                {data.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Release notes panel — above the per-service rows */}
          {releaseNotesState.status === 'loading' && (
            <div className="release-notes-loading" role="status" aria-live="polite">
              <span className="summary-spinner" aria-hidden>⟳</span>
              {' '}Generating release notes… (up to 30s on cold cache)
            </div>
          )}
          {releaseNotesState.status === 'error' && (
            <div className="release-notes-error" role="alert">
              <span>Release notes unavailable: {releaseNotesState.message}</span>
              <button
                type="button"
                className="release-notes-retry-btn"
                onClick={onReleaseNotesRefresh}
              >
                Try again
              </button>
            </div>
          )}
          {releaseNotesState.status === 'loaded' && (
            <ReleaseNotesPanel
              response={releaseNotesState.response}
              onRefresh={onReleaseNotesRefresh}
              onServiceClick={onAttentionServiceClick}
              stale={stale}
            />
          )}

          {data.cells.length === 0 && data.errors.length === 0 && (
            <div className="drift-tab-empty">
              No drift between {data.from_env} and {data.to_env}.{' '}
              <time dateTime={data.generated_at}>{data.generated_at}</time>
            </div>
          )}
          <div className="drift-tab-rows">
            {data.cells.map((cell) => (
              <DriftRow
                key={cell.service}
                cell={cell}
                from={from}
                to={to}
                aggregate={aggregate}
                summaryState={summaryStates[cell.service] ?? { status: 'idle' }}
                setSummaryState={setSummaryStateFor(cell.service)}
                autoExpand={targetService === cell.service}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
